import { httpClient, toClientApiError } from "./httpClient";

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    throw toClientApiError(err);
  }
}

export type FinanceCategory = {
  id: string;
  parentId: string | null;
  name: string;
  description: string | null;
  color: string | null;
  isDefault: boolean;
  isSystem: boolean;
  sortOrder: number;
  excludeFromBalance: boolean;
  monthlyIncomeTarget: number | null;
  annualIncomeTarget: number | null;
  monthlyExpenseBudget: number | null;
  annualExpenseBudget: number | null;
  defaultExpenseAmountAgorot: number | null;
  defaultIncomeAmountAgorot: number | null;
  defaultSubscriptionAmountAgorot: number | null;
  recurringExpenseEnabled: boolean;
  recurringExpenseAmountAgorot: number | null;
  recurringExpenseBillingDay: number | null;
  recurringIncomeEnabled: boolean;
  recurringIncomeAmountAgorot: number | null;
  recurringIncomeBillingDay: number | null;
  parentName?: string | null;
  displayName?: string;
  incomeCount?: number;
  expenseCount?: number;
  donationCount?: number;
  childrenCount?: number;
  incomeTotal?: number;
  expenseTotal?: number;
  donationTotal?: number;
  usageCount?: number;
  subtreeIncomeTotal?: number;
  subtreeExpenseTotal?: number;
  subtreeDonationTotal?: number;
  subtreeUsageCount?: number;
};

export type FinanceFund = {
  id: string;
  customerId: string;
  name: string;
  description: string | null;
  color: string | null;
  isDefault: boolean;
  isSystem: boolean;
  sortOrder: number;
  incomeCount?: number;
  expenseCount?: number;
  incomeTotal?: number;
  expenseTotal?: number;
  transfersIn?: number;
  transfersOut?: number;
  balance?: number;
  usageCount?: number;
};

export type FundTransfer = {
  id: string;
  customerId: string;
  fromFundId: string;
  toFundId: string;
  amountAgorot: number;
  transferDate: string;
  reference: string | null;
  notes: string | null;
};

export type BudgetOverview = {
  period: {
    year: number;
    month: number | null;
    scope: string;
    start: string;
    end: string;
  };
  categories: Array<{
    id: string;
    parentId: string | null;
    name: string;
    displayName: string;
    color: string | null;
    excludeFromBalance: boolean;
    incomeTarget: number;
    incomeActual: number;
    incomeRemaining: number;
    incomeProgressPct: number;
    incomeOnTrack: boolean;
    expenseBudgetBase: number;
    transfersIn: number;
    transfersOut: number;
    effectiveExpenseBudget: number;
    expenseActual: number;
    expenseRemaining: number;
    expenseProgressPct: number;
    isOverBudget: boolean;
    isNearBudget: boolean;
    hasBudgetConfig: boolean;
  }>;
  transfers: Array<{
    id: string;
    customerId: string;
    fromCategoryId: string;
    toCategoryId: string;
    amountAgorot: number;
    periodYear: number;
    periodMonth: number | null;
    notes: string | null;
  }>;
  alerts: Array<{
    type: string;
    severity: string;
    categoryId: string;
    categoryName: string;
    message: string;
    overspend?: number;
  }>;
  totals: {
    incomeTarget: number;
    incomeActual: number;
    expenseBudget: number;
    expenseActual: number;
    overBudgetCount: number;
    excludedCount: number;
    incomeProgressPct: number;
    expenseProgressPct: number;
  };
};

export type CampaignMapping = {
  customerId: string;
  campaignId: string;
  campaignName: string;
  targetAmount: number;
  currentAmount: number;
  isActive: boolean;
  categoryId: string | null;
  categoryName: string | null;
};

export type NedarimMappingRow = {
  id: string | null;
  customerId: string;
  nedarimGroupe: string;
  categoryId: string | null;
  categoryName: string | null;
  donationCount: number;
  isMapped: boolean;
};

function base(customerId: string) {
  return `/structure/customers/${customerId}`;
}

export const financeStructureApi = {
  /** Tenant-shared category catalog (ADR-0019). */
  listCategories() {
    return wrap(async () => {
      const res = await httpClient.get<{ items: FinanceCategory[] }>(
        `/structure/categories`,
      );
      return res.data.items;
    });
  },

  /** @deprecated Alias — categories are tenant-shared; customerId ignored. */
  listCustomerCategories(_customerId?: string) {
    return financeStructureApi.listCategories();
  },

  createCategory(body: Record<string, unknown>) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: FinanceCategory }>(
        `/structure/categories`,
        body,
      );
      return res.data.item;
    });
  },

  updateCategory(categoryId: string, body: Record<string, unknown>) {
    return wrap(async () => {
      const res = await httpClient.patch<{ item: FinanceCategory }>(
        `/structure/categories/${categoryId}`,
        body,
      );
      return res.data.item;
    });
  },

  setDefaultCategory(categoryId: string) {
    return wrap(async () => {
      const res = await httpClient.put<{ item: FinanceCategory }>(
        `/structure/categories/${categoryId}/default`,
      );
      return res.data.item;
    });
  },

  deleteCategory(categoryId: string) {
    return wrap(async () => {
      await httpClient.delete(`/structure/categories/${categoryId}`);
    });
  },

  mergeCategories(body: Record<string, unknown>) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: FinanceCategory }>(
        `/structure/categories/merge`,
        body,
      );
      return res.data.item;
    });
  },

  importCategories(
    rows: Array<{
      name: string;
      parentName?: string | null;
      description?: string | null;
      color?: string | null;
      sortOrder?: number | null;
    }>,
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{
        created: number;
        skipped: number;
        errors: string[];
      }>(`/structure/categories/import`, { rows });
      return res.data;
    });
  },

  previewCategoriesImportFile(file: File) {
    return wrap(async () => {
      const form = new FormData();
      form.append("file", file);
      const res = await httpClient.post<{
        totalRows: number;
        estimatedCreate: number;
        estimatedSkip: number;
        unmappedHeaders: string[];
        errors: Array<{ line: number; message: string }>;
        sample: Array<Record<string, unknown>>;
        rows: Array<{
          name: string;
          parentName?: string | null;
          description?: string | null;
          color?: string | null;
          sortOrder?: number | null;
        }>;
      }>(`/structure/categories/import/preview`, form, {
        headers: { "Content-Type": undefined },
        transformRequest: [
          (data, headers) => {
            if (headers && typeof headers === "object" && "delete" in headers) {
              (headers as { delete: (key: string) => void }).delete("Content-Type");
            } else if (headers && typeof headers === "object") {
              delete (headers as Record<string, unknown>)["Content-Type"];
              delete (headers as Record<string, unknown>)["content-type"];
            }
            return data;
          },
        ],
      });
      return res.data;
    });
  },

  manualAdjust(body: Record<string, unknown>) {
    return wrap(async () => {
      const res = await httpClient.post(`/structure/categories/manual-adjust`, body);
      return res.data;
    });
  },

  listRoutingRules(categoryId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: unknown[] }>(
        `/structure/categories/${categoryId}/routing-rules`,
      );
      return res.data.items;
    });
  },

  createRoutingRule(body: Record<string, unknown>) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: unknown }>(
        `/structure/routing-rules`,
        body,
      );
      return res.data.item;
    });
  },

  deleteRoutingRule(ruleId: string) {
    return wrap(async () => {
      await httpClient.delete(`/structure/routing-rules/${ruleId}`);
    });
  },

  listFunds() {
    return wrap(async () => {
      const res = await httpClient.get<{ items: FinanceFund[] }>(`/structure/funds`);
      return res.data.items;
    });
  },

  createFund(customerId: string, body: Record<string, unknown>) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: FinanceFund }>(
        `${base(customerId)}/funds`,
        body,
      );
      return res.data.item;
    });
  },

  updateFund(customerId: string, fundId: string, body: Record<string, unknown>) {
    return wrap(async () => {
      const res = await httpClient.patch<{ item: FinanceFund }>(
        `${base(customerId)}/funds/${fundId}`,
        body,
      );
      return res.data.item;
    });
  },

  setDefaultFund(customerId: string, fundId: string) {
    return wrap(async () => {
      const res = await httpClient.put<{ item: FinanceFund }>(
        `${base(customerId)}/funds/${fundId}/default`,
      );
      return res.data.item;
    });
  },

  deleteFund(customerId: string, fundId: string) {
    return wrap(async () => {
      await httpClient.delete(`${base(customerId)}/funds/${fundId}`);
    });
  },

  listFundTransfers() {
    return wrap(async () => {
      const res = await httpClient.get<{ items: FundTransfer[] }>(
        `/structure/fund-transfers`,
      );
      return res.data.items;
    });
  },

  createFundTransfer(customerId: string, body: Record<string, unknown>) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: FundTransfer }>(
        `${base(customerId)}/fund-transfers`,
        body,
      );
      return res.data.item;
    });
  },

  deleteFundTransfer(customerId: string, transferId: string) {
    return wrap(async () => {
      await httpClient.delete(`${base(customerId)}/fund-transfers/${transferId}`);
    });
  },

  budgetOverview(params: {
    year?: number;
    month?: number;
    scope?: "month" | "year";
  }) {
    return wrap(async () => {
      const res = await httpClient.get<BudgetOverview>(
        `/structure/category-budgets/overview`,
        { params },
      );
      return res.data;
    });
  },

  createBudgetTransfer(customerId: string, body: Record<string, unknown>) {
    return wrap(async () => {
      const res = await httpClient.post(
        `${base(customerId)}/category-budgets/transfers`,
        body,
      );
      return res.data;
    });
  },

  deleteBudgetTransfer(customerId: string, transferId: string) {
    return wrap(async () => {
      await httpClient.delete(
        `${base(customerId)}/category-budgets/transfers/${transferId}`,
      );
    });
  },

  listCampaignMappings() {
    return wrap(async () => {
      const res = await httpClient.get<{ items: CampaignMapping[] }>(
        `/structure/campaign-mappings`,
      );
      return res.data.items;
    });
  },

  createCampaign(customerId: string, body: Record<string, unknown>) {
    return wrap(async () => {
      const res = await httpClient.post(`${base(customerId)}/campaigns`, body);
      return res.data;
    });
  },

  setCampaignMapping(
    customerId: string,
    campaignId: string,
    categoryId: string | null,
  ) {
    return wrap(async () => {
      const res = await httpClient.put(
        `${base(customerId)}/campaign-mappings/${campaignId}`,
        { categoryId },
      );
      return res.data;
    });
  },

  listNedarimMappings() {
    return wrap(async () => {
      const res = await httpClient.get<{
        rows: NedarimMappingRow[];
        mapped: NedarimMappingRow[];
        unmapped: NedarimMappingRow[];
      }>(`/structure/nedarim-mappings`);
      return res.data;
    });
  },

  createNedarimMapping(customerId: string, body: Record<string, unknown>) {
    return wrap(async () => {
      const res = await httpClient.post(
        `${base(customerId)}/nedarim-mappings`,
        body,
      );
      return res.data;
    });
  },

  updateNedarimMapping(
    customerId: string,
    mappingId: string,
    categoryId: string,
  ) {
    return wrap(async () => {
      const res = await httpClient.put(
        `${base(customerId)}/nedarim-mappings/${mappingId}`,
        { categoryId },
      );
      return res.data;
    });
  },

  deleteNedarimMapping(customerId: string, mappingId: string) {
    return wrap(async () => {
      await httpClient.delete(`${base(customerId)}/nedarim-mappings/${mappingId}`);
    });
  },
};

export function formatAgorot(agorot: number): string {
  return (agorot / 100).toLocaleString("he-IL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function shekelsToAgorot(shekels: number): number {
  return Math.round(shekels * 100);
}
